import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

type InvalidationCallback = () => void;

interface ReconnectInvalidationRegistry {
  register: (cb: InvalidationCallback) => () => void;
  invokeAll: () => void;
}

const ReconnectInvalidationContext =
  createContext<ReconnectInvalidationRegistry | null>(null);

/**
 * Provider qui maintient un registre de callbacks d'invalidation à appeler lors
 * d'une reconnexion WebSocket. Permet à n'importe quel composant de s'inscrire
 * pour invalider ses caches quand la connexion WS est rétablie.
 */
export const ReconnectInvalidationProvider: React.FC<{
  children: React.ReactNode;
}> = ({children}) => {
  const callbacksRef = useRef(new Set<InvalidationCallback>());

  // useState pour créer la valeur une seule fois (stable entre les renders)
  const [registry] = useState<ReconnectInvalidationRegistry>(() => ({
    register: (cb: InvalidationCallback) => {
      callbacksRef.current.add(cb);
      return () => {
        callbacksRef.current.delete(cb);
      };
    },
    invokeAll: () => {
      callbacksRef.current.forEach((cb) => {
        cb();
      });
    },
  }));

  return (
    <ReconnectInvalidationContext.Provider value={registry}>
      {children}
    </ReconnectInvalidationContext.Provider>
  );
};

/**
 * Hook pour enregistrer un callback d'invalidation lors de la reconnexion WebSocket.
 * Le callback est automatiquement désinscrit au démontage du composant.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useRegisterReconnectInvalidation(
  callback: InvalidationCallback,
) {
  const registry = useContext(ReconnectInvalidationContext);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  // Wrapper stable qui délègue à la ref
  const stableCallback = useCallback(() => {
    callbackRef.current();
  }, []);

  useEffect(() => {
    if (!registry) return;
    return registry.register(stableCallback);
  }, [registry, stableCallback]);
}

/**
 * Hook interne pour récupérer la fonction invokeAll (utilisé par HaliWebSocket).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useReconnectInvalidation(): (() => void) | undefined {
  const registry = useContext(ReconnectInvalidationContext);
  return registry?.invokeAll;
}
